import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Target, Pill, CalendarCheck, Printer, RefreshCw, AlertOctagon, ClipboardList, HeartPulse, Scale } from 'lucide-react';
import { openPrintWindow } from '@/lib/letterhead';
import RilevazionePesiDialog from '@/components/RilevazionePesiDialog';
import { toast } from 'sonner';

const GRUPPI = [
  { key: 'pai_rivalutazioni', label: 'Rivalutazioni PAI', icon: ClipboardList, color: 'text-amber-500' },
  { key: 'obiettivi_pai', label: 'Obiettivi PAI', icon: Target, color: 'text-violet-500' },
  { key: 'terapie', label: 'Fine Terapie', icon: Pill, color: 'text-blue-500' },
  { key: 'appuntamenti', label: 'Appuntamenti da Effettuare', icon: CalendarCheck, color: 'text-cyan-500' },
];

export default function Scadenze() {
  const navigate = useNavigate();
  const [giorni, setGiorni] = useState('30');
  const [area, setArea] = useState('tutte');
  const [d, setD] = useState(null);
  const [ecg, setEcg] = useState(null);
  const [peso, setPeso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPesi, setShowPesi] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, ecgRes, pesoRes] = await Promise.all([
        api.get('/report/scadenze', { params: { giorni: Number(giorni), area } }),
        api.get('/ecg/controlli-programmati', { params: { area } }),
        api.get('/analisi/peso-alert', { params: { area } }),
      ]);
      setD(res.data);
      setEcg(ecgRes.data);
      setPeso(pesoRes.data);
    } catch { setD(null); toast.error('Errore nel caricamento delle scadenze'); }
    setLoading(false);
  }, [giorni, area]);

  useEffect(() => { load(); }, [load]);

  const handlePrint = () => {
    if (!d) return;
    let body = `<div class="info-grid">
      <div class="info-item"><span class="info-label">Orizzonte</span> <span class="info-value">prossimi ${d.giorni} giorni</span></div>
      <div class="info-item"><span class="info-label">Totale scadenze</span> <span class="info-value">${d.totale}</span></div>
      <div class="info-item"><span class="info-label">Gia scadute</span> <span class="info-value">${d.scaduti}</span></div>
      <div class="info-item"><span class="info-label">Area</span> <span class="info-value">${area === 'tutte' ? 'Tutte' : area}</span></div>
    </div>`;
    GRUPPI.forEach(g => {
      const rows = d[g.key] || [];
      if (!rows.length) return;
      body += `<p class="section-title">${g.label}</p><table><thead><tr><th>Ospite</th><th>Camera</th><th>Dettaglio</th><th>Scadenza</th><th>Giorni</th></tr></thead><tbody>`;
      rows.forEach(x => {
        body += `<tr><td>${x.ospite_nome}</td><td>${x.stanza || ''}</td><td>${x.descrizione || ''}</td><td>${x.data_scadenza}</td><td>${x.scaduto ? `<span class="badge badge-red">scaduto ${Math.abs(x.giorni)} gg</span>` : `<span class="badge badge-amber">${x.giorni} gg</span>`}</td></tr>`;
      });
      body += `</tbody></table>`;
    });
    const ecgRows = (ecg?.controlli || []).filter(c => c.stato !== 'ok');
    if (ecgRows.length) {
      body += `<p class="section-title">Controlli ECG periodici (farmaci che allungano il QT)</p><table><thead><tr><th>Ospite</th><th>Camera</th><th>Farmaci</th><th>Ultimo ECG</th><th>Prossimo controllo</th></tr></thead><tbody>`;
      ecgRows.forEach(c => {
        body += `<tr><td>${c.ospite_nome}</td><td>${c.stanza || ''}</td><td>${c.farmaci_qt.map(f => f.farmaco).join(', ')}</td><td>${c.ultimo_ecg || 'nessuno'}</td><td>${c.prossimo_controllo} ${c.stato === 'scaduto' ? '<span class="badge badge-red">scaduto</span>' : '<span class="badge badge-amber">in scadenza</span>'}</td></tr>`;
      });
      body += `</tbody></table>`;
    }
    if (peso?.alert?.length) {
      body += `<p class="section-title">Rischio nutrizionale (peso / BMI)</p><table><thead><tr><th>Ospite</th><th>Camera</th><th>Peso</th><th>BMI</th><th>Motivo</th></tr></thead><tbody>`;
      peso.alert.forEach(p => {
        body += `<tr><td>${p.ospite_nome}</td><td>${p.stanza || ''}</td><td>${p.peso_attuale} kg</td><td>${p.bmi ?? '-'}</td><td>${p.rischio_motivi.join('; ')}</td></tr>`;
      });
      body += `</tbody></table>`;
    }
    openPrintWindow('Scadenzario Clinico', body, { subtitle: `Prossimi ${d.giorni} giorni` });
  };

  return (
    <div className="space-y-5 max-w-[1600px] mx-auto" data-testid="scadenze-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Scadenzario</h1>
          <p className="text-sm text-slate-500">Rivalutazioni, obiettivi, terapie e appuntamenti in scadenza</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={giorni} onValueChange={setGiorni}>
            <SelectTrigger className="w-36" data-testid="scadenze-giorni-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 giorni</SelectItem>
              <SelectItem value="15">15 giorni</SelectItem>
              <SelectItem value="30">30 giorni</SelectItem>
              <SelectItem value="60">60 giorni</SelectItem>
              <SelectItem value="90">90 giorni</SelectItem>
            </SelectContent>
          </Select>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger className="w-36" data-testid="scadenze-area-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutte">Tutte le aree</SelectItem>
              <SelectItem value="verde">Area Verde</SelectItem>
              <SelectItem value="gialla">Area Gialla</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="scadenze-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={handlePrint} data-testid="scadenze-print-btn"><Printer className="w-4 h-4 mr-1" />Stampa</Button>
        </div>
      </div>

      {loading ? <div className="text-center py-12 text-slate-400">Caricamento...</div> : !d ? (
        <div className="text-center py-12 text-slate-400">Nessun dato</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="scadenze-riepilogo">
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Totale scadenze</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="scadenze-totale">{d.totale}</p>
            </div>
            <div className="bg-white border border-red-200 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-wide text-red-400 flex items-center gap-1"><AlertOctagon className="w-3 h-3" />Gia scadute</p>
              <p className="text-2xl font-bold text-red-600" data-testid="scadenze-scaduti">{d.scaduti}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Rivalutazioni PAI</p>
              <p className="text-2xl font-bold text-amber-600">{d.pai_rivalutazioni.length}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Appuntamenti</p>
              <p className="text-2xl font-bold text-cyan-600">{d.appuntamenti.length}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="scadenze-card-ecg-controlli">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <HeartPulse className="w-4 h-4 text-rose-500" />Controlli ECG periodici (farmaci che allungano il QT)
                  <Badge variant="outline" className="text-[10px] ml-auto">{ecg?.controlli?.length || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[10px] text-slate-400 mb-2">Periodicita 6 mesi · promemoria 15 giorni prima della scadenza</p>
                {!ecg?.controlli?.length ? <p className="text-xs text-slate-400 py-4 text-center">Nessun ospite in terapia con farmaci che allungano il QT</p> : (
                  <div className="space-y-2 max-h-[340px] overflow-y-auto">
                    {ecg.controlli.map((c, i) => (
                      <button key={i} onClick={() => navigate(`/ospiti/${c.ospite_id}`)}
                        className={`w-full text-left p-2.5 rounded-lg border text-xs transition-colors hover:bg-slate-100 ${c.stato === 'scaduto' ? 'bg-red-50 border-red-200' : c.stato === 'in_scadenza' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
                        data-testid={`scadenza-ecg-${i}`}>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium text-slate-800">{c.ospite_nome}{c.stanza && <span className="text-slate-400 font-normal"> · cam. {c.stanza}</span>}</span>
                          <Badge className={`text-[9px] flex-shrink-0 ${c.stato === 'scaduto' ? 'bg-red-100 text-red-700' : c.stato === 'in_scadenza' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                            {c.mai_eseguito ? 'mai eseguito' : c.stato === 'scaduto' ? `scaduto ${Math.abs(c.giorni_residui)} gg` : `${c.giorni_residui} gg`}
                          </Badge>
                        </div>
                        <p className="text-slate-500 mt-0.5">{c.farmaci_qt.map(f => f.farmaco).join(', ')}</p>
                        <p className="text-slate-400 mt-0.5">Ultimo ECG: {c.ultimo_ecg || 'nessuno'} · prossimo controllo: {c.prossimo_controllo}</p>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid="scadenze-card-peso">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Scale className="w-4 h-4 text-fuchsia-500" />Rischio nutrizionale (peso / BMI)
                  <Badge variant="outline" className="text-[10px] ml-auto">{peso?.totale || 0}</Badge>
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setShowPesi(true)} data-testid="scadenze-rilevazione-pesi-btn">Rilevazione peso</Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!peso?.alert?.length ? <p className="text-xs text-slate-400 py-4 text-center">Nessun ospite a rischio nutrizionale</p> : (
                  <div className="space-y-2 max-h-[340px] overflow-y-auto">
                    {peso.alert.map((p, i) => (
                      <button key={i} onClick={() => navigate(`/ospiti/${p.ospite_id}`)}
                        className={`w-full text-left p-2.5 rounded-lg border text-xs transition-colors hover:bg-slate-100 ${p.rischio_livello === 'alta' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}
                        data-testid={`scadenza-peso-${i}`}>
                        <div className="flex justify-between gap-2">
                          <span className="font-medium text-slate-800">{p.ospite_nome}{p.stanza && <span className="text-slate-400 font-normal"> · cam. {p.stanza}</span>}</span>
                          <Badge className={`text-[9px] flex-shrink-0 ${p.rischio_livello === 'alta' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{p.rischio_livello}</Badge>
                        </div>
                        <p className="text-slate-500 mt-0.5">{p.peso_attuale} kg{p.bmi ? ` · BMI ${p.bmi} (${p.bmi_categoria})` : ''} · ultima rilevazione {p.data_ultima_rilevazione}</p>
                        <p className="text-slate-600 mt-0.5">{p.rischio_motivi[0]}</p>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {GRUPPI.map(g => {
              const rows = d[g.key] || [];
              return (
                <Card key={g.key} data-testid={`scadenze-card-${g.key}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <g.icon className={`w-4 h-4 ${g.color}`} />{g.label}
                      <Badge variant="outline" className="text-[10px] ml-auto">{rows.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {rows.length === 0 ? <p className="text-xs text-slate-400 py-4 text-center">Nessuna scadenza</p> : (
                      <div className="space-y-2 max-h-[340px] overflow-y-auto">
                        {rows.map((x, i) => (
                          <button
                            key={i}
                            onClick={() => navigate(`/ospiti/${x.ospite_id}`)}
                            className={`w-full text-left p-2.5 rounded-lg border text-xs transition-colors hover:bg-slate-100 ${x.scaduto ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}
                            data-testid={`scadenza-${g.key}-${i}`}
                          >
                            <div className="flex justify-between gap-2">
                              <span className="font-medium text-slate-800">{x.ospite_nome}{x.stanza && <span className="text-slate-400 font-normal"> · cam. {x.stanza}</span>}</span>
                              <Badge className={`text-[9px] flex-shrink-0 ${x.scaduto ? 'bg-red-100 text-red-700' : x.giorni <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                {x.scaduto ? `scaduto ${Math.abs(x.giorni)} gg` : `${x.giorni} gg`}
                              </Badge>
                            </div>
                            {x.descrizione && <p className="text-slate-500 mt-0.5">{x.descrizione}</p>}
                            <p className="text-slate-400 mt-0.5">Scadenza: {x.data_scadenza}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      <RilevazionePesiDialog open={showPesi} onClose={() => setShowPesi(false)} onSaved={load} />
    </div>
  );
}
